<script setup>
    import { useDebounceFn } from '@vueuse/core';
    const { loggedIn, user, fetch: refreshSession } = useUserSession()
    import { close_button_ui_styles, modal_ui_styles } from '@/utilities/ui';    
    import { useWaitingMessages } from '@/composables/helpers/useWaitingMessages';

    const { $bus } = useNuxtApp();

    const open = defineModel('open');
    const router = useRouter();
    const route = useRoute();
    const error = ref(false);

    const props = defineProps({
        session_id: {
            type: String,
            default: '',
            required: true
        }
    });

    const { messages: waiting, getMessage } = useWaitingMessages();

    const message = ref({})
    const count = ref(0);
    const isRegistering = ref(false);

    onMounted(() => {
        const interval = setInterval(() => {
            count.value = (count.value + 1) % waiting.length;
        }, 10000);
        onUnmounted(() => {
            clearInterval(interval);
        });
    });

    const handleSeeSeats = async () => {
        await refreshSession()
        await navigateTo('/sessions/?tab=seats&view=list');
        setTimeout(() => { open.value = false; }, 1000);
    };

    const registerForSession = useDebounceFn(async () => {
        if (!props.session_id) return;
        isRegistering.value = true;
        const now = Date.now();
        try {
            message.value = await $fetch(`/api/v2/reservations/register?ts=${now}&u=${user.value.ticketId}`, {
                method: 'POST',
                body: { session_id: props.session_id, ts: Date.now() }
            });
            await refreshSession()            
        } catch (e) {
            console.error('Error registering for session:', e);
        } finally {
            $bus.emit('sessions:registered', props.session_id);            
            isRegistering.value = false;
        }
    
    }, 2500);

    watch(open, (val) => {
        if (val) {
            $bus.emit('drawer-open');
            if (props.session_id && loggedIn.value && user.value) {
                isRegistering.value = true;
                registerForSession();
            } else {
                error.value = true;
            }
        } else {
            $bus.emit('drawer-close');
            const currentQuery = { ...route.query };
            delete currentQuery.modal;
            router.replace({ query: currentQuery });
        }
    });
</script>

<template>
    <UModal
        v-model:open="open"
        title="Save a Seat"
        :ui="modal_ui_styles"
        :dismissable="false"
        :close="close_button_ui_styles"
        class="reservations-modal">
        <template #body>
            <div class="flex flex-col items-center justify-center inner" v-if="!isRegistering && !error">
                <h1 class="h1" v-html="message.title"></h1>
                <div class="warning-banner" v-if="message.copy && message.type === 'warning'">
                    <UIcon name="local-warning" class="warning-icon" />
                    <p v-html="message.copy"></p>
                </div>
                <div class="success" v-if="message.copy && message.type === 'success'">
                    <p v-html="message.copy"></p>
                </div>
                <div class="actions">
                    <UButton label="Close" variant="outline-close" color="orange" @click="open = false" />
                    <UButton label="View My Saved Seats" variant="outline-close" @click="handleSeeSeats" />
                </div>
            </div>
            <div class="flex flex-col items-center justify-center inner spinner" v-else-if="!error && isRegistering">
                <FragmentsSpinner />
                <h1 class="h1">Working on your request!</h1>
                <div class="warning-banner">
                    <UIcon name="local-warning" class="warning-icon" />
                    <p>Please don't close this window or you may lose your spot!</p>
                </div>
                <p class="spin-text">{{ getMessage(count) }}</p>
            </div>
            <div class="flex flex-col items-center justify-center inner" v-else>
                <h1 class="h1">A wild error appeared!</h1>
                <div class="warning-banner">
                    <UIcon name="local-warning" class="warning-icon" />
                    <p>Something has gone wrong. Refreshing the page and trying again usually works.</p>
                </div>
                <div class="actions">
                    <UButton label="Close" variant="outline-close" color="orange" @click="open = false" />
                </div>
            </div>            
        </template>
    </UModal>
</template>

<style lang="scss" scoped>
    .reservations-modal {
        color: var(--teal-13);

        .inner {
            max-width: 41.875rem;
            width: 100%;
            padding-bottom: 2.5rem;

            &.spinner {
                gap: 1.6875rem;

                .warning-banner {
                    margin-bottom: 0;
                }

                .h1 {
                    margin-bottom: 0;
                }

                .spin-text {
                    color: var(--teal);
                    font-size: 1.0625rem;
                    font-style: normal;
                    font-weight: 300;
                    line-height: 1.75rem;
                    letter-spacing: 0.02125rem;
                }
            }

            .actions {
                display: flex;
                flex-direction: row;
                gap: 1.5625rem;
            }

            h1 {
                text-align: center;
                font-size: 2rem;
                font-style: normal;
                font-weight: 500;
                line-height: 2.5rem;
                letter-spacing: -0.04rem;
                margin-bottom: 2rem;
                text-wrap: balance;
            }

            .warning-icon {
                width: 1.5rem;
                height: 1.5rem;
                flex-shrink: 0;
            }

            .warning-banner {
                background-color: var(--orange-ghost);
                border-radius: var(--radius);
                padding: 1.8125rem;
                color: var(--orange-ghost-dark);
                font-size: 0.875rem;
                font-style: normal;
                font-weight: 400;
                line-height: 1.25rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                margin-bottom: 2.8125rem;
                letter-spacing: 0;
                width: 100%;

                @media (min-width: $tablet) {
                    flex-direction: row;
                    align-items: center;
                    gap: 1.644375rem;
                }
            }
        }
    }
</style>
